import {isApiError} from '@shared/helpers/api'
import {qs} from '@shared/helpers/queryString'
import {SchemasOrderResult} from '@shared/schemas/order'

import {changeLocation} from '@web/actions/application'
import {
    navigateToOrderDetail,
    navigateToShoppingMySubscription,
    navigateToShoppingSubscriptionFail,
} from '@web/actions/navigateTo'
import {saveStateToStorage} from '@web/actions/restoreState'
import {ReasonTypeType, OrderEntryType, ResultChannelGroupType} from '@web/core/constants'
import {DEFAULT_PRODUCT_URL, DEFAULT_PRODUCT_URL_M, getEasyBookingResult} from '@web/core/urls'
import getApi, {createApiAsyncThunk, createWrapper} from '@web/helpers/api'
import {Sentry} from '@web/helpers/log'
import {isMobileDisplay} from '@web/helpers/userAgent'
import {CommonState} from '@web/slices/common/slice'
import {ResultState} from '@web/slices/result/slice'

import {BaseResultInfoResponse, ResultInfoRedirectResponse, type SubscriptionResultInfoResponse} from './apis'

/** 주문결과 API URL 생성 */
const api = getApi('/orderApi/orderSheet/result')
const subscriptionApi = getApi('/orderApi/orderSheet/subscription/result')

/** 가맹점(mall, 블로그 카페 포함), 스마트스토어(seller) 주문완료 정보 조회 액션 */
export const getResultInfo = createApiAsyncThunk<
    BaseResultInfoResponse & {
        defaultProductUrl: string
    },
    {
        orderResultID: string
        channelGroup: ResultChannelGroupType
    },
    {
        state: {
            common: CommonState
            result: ResultState
        }
        rejectValue: {
            reason?: 'REDIRECT' | ReasonTypeType
            channel?: OrderEntryType
            message: string
        }
    }
>('result/getResultInfo', async ({orderResultID, channelGroup}, {dispatch, rejectWithValue}) => {
    const response = await createWrapper(
        api.get<{result: SchemasOrderResult['/']['response'] | ResultInfoRedirectResponse}>(
            qs.stringifyUrl({
                url: '/',
                query: {
                    orderNo: orderResultID,
                    requesterType: channelGroup,
                },
            }),
            {
                validateStatus: (status) => {
                    return status === 302 || status < 300
                },
            },
        ),
    )

    if ('redirectType' in response) {
        dispatch(
            navigateToOrderDetail({
                historyType: 'REPLACE',
                orderNo: orderResultID,
                message:
                    '유효기한 경과 & 주문상태가 변경되어 주문상세로 이동합니다. 주문하신 정보는 주문상세에서 확인하실 수 있습니다.',
            }),
        )
        return rejectWithValue({
            message: '클레임 상품이 포함으로 주문상세 이동',
            reason: 'REDIRECT',
        })
    }

    return {
        ...response,
        order: {
            ...response.order,
            saleChannelType: response.order.saleChannelType as BaseResultInfoResponse['order']['saleChannelType'],
        },
        defaultProductUrl: isMobileDisplay ? DEFAULT_PRODUCT_URL_M : DEFAULT_PRODUCT_URL,
    }
})

/**
 * 주문완료 AML Token 조회
 */
export const getAmlToken = createApiAsyncThunk<
    SchemasOrderResult['/amlToken']['response'],
    void,
    {
        state: {
            result: ResultState
        }
    }
>('result/getAmlToken', () => {
    return createWrapper<SchemasOrderResult['/amlToken']['response']>(api.post('/amlToken'))
})

/**
 * 계좌간편결제 입금 계좌 정보 조회
 */
export const getTradeAccountInfo = createApiAsyncThunk<
    SchemasOrderResult['/bankEasyPaysTradeAccounts']['response'],
    void,
    {
        state: {
            result: ResultState
        }
    }
>('result/getTradeAccountInfo', async (_, {getState}) => {
    const {
        result: {
            order: {orderNo},
        },
    } = getState()

    return createWrapper<SchemasOrderResult['/bankEasyPaysTradeAccounts']['response']>(
        api.get(
            qs.stringifyUrl({
                url: '/bankEasyPaysTradeAccounts',
                query: {
                    orderNo,
                },
            }),
        ),
    )
})

/** 장바구니 담은 상품 개수 조회 */
export const getCartCount = createApiAsyncThunk<
    number,
    void,
    {
        state: {
            result: ResultState
        }
    }
>('result/getCartCount', async () => {
    const result = await createWrapper<SchemasOrderResult['/cart/count']['response']>(api.get('/cart/count'))

    return result.value
})

/**
 * 패밀리결제 알림
 */
export const notifyFamilyPay = createApiAsyncThunk<
    void,
    {
        orderNo: string
        notificationType: 'BIZTALK' | 'SMS' | 'KAKAO_TALK'
        memo?: string
    }
>('result/notifyFamilyPay', async ({orderNo, notificationType, memo}) => {
    // 카카오톡 구현 필요
    if (notificationType === 'KAKAO_TALK') {
        return
    }

    const result = await createWrapper<SchemasOrderResult['/familyPay/notify']['response']>(
        api.post('/familyPay/notify', {
            orderNo,
            familyPayNotificationType: notificationType,
            familyPayRequestMemo: memo,
        }),
    )

    return result
})

/**
 * 패밀리결제 알림
 */
export const validNotifyFamilyPay = createApiAsyncThunk<
    {
        valid: boolean
    },
    {
        orderNo: string
    },
    {
        state: unknown
    }
>('result/validNotifyFamilyPay', async ({orderNo}) => {
    const result = await createWrapper<SchemasOrderResult['/familyPay/status']['response']>(
        api.get('/familyPay/status', {
            params: {orderNo},
        }),
    )

    return {
        valid: !(result.orderStatusType === 'PAYED' || result.orderStatusType === 'CANCELED_BY_NOPAYMENT'),
    }
})

/** 예약/로컬페이 주문완료 정보 조회 액션 (외부 주문완료로 이동위한 조회) */
export const redirectToEasyBookingResult = createApiAsyncThunk<void, string, object>(
    'result/getEasyBookingResultInfo',
    async (orderNo, {dispatch}) => {
        const {itemMemo, productOrderNo, productApiType} = await createWrapper<
            SchemasOrderResult['/easybooking']['response']
        >(
            api.get(
                qs.stringifyUrl({
                    url: '/easybooking',
                    query: {
                        orderNo,
                    },
                }),
            ),
        )

        dispatch(
            changeLocation({
                type: 'REPLACE',
                url: getEasyBookingResult({
                    orderNo,
                    productApiType,
                    productOrderNo,
                    itemOptionCode: itemMemo,
                }),
            }),
        )
    },
)

/** 정기구독 신청완료 정보 조회 액션 */
export const getShoppingSubscriptionResultInfo = createApiAsyncThunk<
    SubscriptionResultInfoResponse,
    {subscriptionId: string},
    /** navigateTo action 사용을 위한 주입 */
    {
        state: {
            common: CommonState
        }
        rejectValue: {
            reason?: 'REDIRECT' | ReasonTypeType
            message: string
            scheduleId?: number
            deliveryId?: number
        }
    }
>('result/getShoppingSubscriptionResultInfo', async ({subscriptionId}, {dispatch, rejectWithValue}) => {
    try {
        const url = qs.stringifyUrl({
            url: `/info`,
            query: {subscriptionId},
        })
        const {
            data: {result: response},
        } = await subscriptionApi.get<{result: SubscriptionResultInfoResponse}>(url)

        return response
    } catch (e) {
        Sentry.sendGeneralError(new Error('신청완료 조회 실패'), e)

        if (isApiError(e)) {
            if (e?.response?.data?.result?.errorType === 'EXPIRED_SUBSCRIPTION_RESULT') {
                const errorData = e.response.data.result.data
                dispatch(
                    navigateToShoppingMySubscription({
                        forceLocationChange: true,
                        deliveryId: errorData?.deliveryId ?? '',
                        scheduleId: errorData?.scheduleId?.toString() ?? '',
                    }),
                )
                return rejectWithValue({
                    message: '만료된 주소입니다. 정기구독 상세 페이지로 이동합니다.',
                    reason: 'REDIRECT',
                })
            } else {
                /** 잘못된 신청완료 번호 접근 시 으로 정기구독 실패페이지 이동처리 */
                saveStateToStorage<{
                    /** 실패 코드 */
                    failCode: string
                    /** 리턴 url */
                    rurl?: string
                }>({
                    currentState: {failCode: 'SUBSCRIPTION_RESULT_FETCH_FAIL'},
                    secureStorageKey: 'orders.paymentmethod.shopping.fail',
                    saverKey: subscriptionId,
                })
                dispatch(navigateToShoppingSubscriptionFail())
                return rejectWithValue({
                    message: '올바르지 못한 경로로 접근했습니다. 정기구독 실패 페이지로 이동합니다.',
                    reason: 'REDIRECT',
                })
            }
        }

        throw e
    }
})
